import {
	INTERVAL_LENGTH,
	PATIENT_SERVICES_BASE_URL,
	PREFIX,
	TIMEOUT_WARNING_BUFFER,
	TIMEOUT_WARNING_LENGTH,
} from '@src/constants';
import { logout } from '@src/services/patient-services/Logout';
import { useAppStore } from '@src/store';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

interface SessionWarningState {
	show: boolean;
	timeLeft: number;
}

interface TimeLeftResult {
	timeLeft: number;
	expired: boolean;
	error?: string;
	triggerLogout?: boolean;
}

interface ExtendSessionResult {
	timeLeft: number;
	success: boolean;
	error?: string;
	triggerLogout?: boolean;
}

export const usePortalSessionManager = () => {
	const eapiBaseUrl = useAppStore((store) => store.eapiBaseUrl);
	const warningThreshold = TIMEOUT_WARNING_LENGTH + TIMEOUT_WARNING_BUFFER;
	const preWarningLength = (INTERVAL_LENGTH - TIMEOUT_WARNING_LENGTH) * 1000;
	const disabled = process.env.NEXT_PUBLIC_DISABLE_REDIS_SESSIONS === 'true';

	const { pathname, push, query } = useRouter();
	const { appointmentId: visitId } = query;

	const lastSuccessfulExtensionRef = useRef<number>(0);
	const [shouldLogout, setShouldLogout] = useState(false);
	const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

	const [sessionWarning, setSessionWarning] = useState<SessionWarningState>({
		show: false,
		timeLeft: TIMEOUT_WARNING_LENGTH,
	});

	const timeLeft = useCallback(async (): Promise<TimeLeftResult> => {
		try {
			const response = await fetch(`${PREFIX}/session-time-left/`, {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (response.ok) {
				const data = await response.json();
				return {
					expired: data.expired,
					timeLeft: data.timeLeft,
					triggerLogout: data.triggerLogout,
				};
			} else {
				const data = await response.json().catch(() => ({
					message: `HTTP ${response.status}: ${response.statusText}`,
				}));

				return {
					timeLeft: 0,
					expired: true,
					triggerLogout: data.triggerLogout || response.status === 401,
					error: data.error || `HTTP ${response.status}: ${response.statusText}`,
				};
			}
		} catch (caughtError) {
			const errorMessage = caughtError instanceof Error ? caughtError.message : String(caughtError);
			return {
				timeLeft: 0,
				expired: true,
				error: errorMessage,
				triggerLogout: false,
			};
		}
	}, []);

	const isOnPatientServicesApp = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}`);
	const isOnPatientServicesLogin =
		pathname === `/${PATIENT_SERVICES_BASE_URL}` || pathname === `/${PATIENT_SERVICES_BASE_URL}/`;
	const isOnPatientServicesAuthentication = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/authentication`);
	const isOnPatientServicesMagicLinkExpiration = pathname.includes(`/${PATIENT_SERVICES_BASE_URL}/expiration`);
	const isOneClick = !!visitId;

	const isSessionManagementActive =
		!disabled &&
		isOnPatientServicesApp &&
		!isOnPatientServicesLogin &&
		!isOnPatientServicesAuthentication &&
		!isOnPatientServicesMagicLinkExpiration &&
		!isOneClick;

	const startSession = useCallback(() => {
		timeLeft().then((result) => {
			if (result.triggerLogout) {
				setShouldLogout(true);
			}
		});

		sessionCheckIntervalRef.current = setInterval(async () => {
			const result = await timeLeft();

			if (result.triggerLogout) {
				setShouldLogout(true);
			}
		}, preWarningLength);
	}, [isSessionManagementActive, timeLeft, warningThreshold, preWarningLength]);

	const resetSession = useCallback(() => {
		if (sessionCheckIntervalRef.current) {
			clearInterval(sessionCheckIntervalRef.current);
		}

		sessionCheckIntervalRef.current = setInterval(async () => {
			const result = await timeLeft();

			if (result.triggerLogout) {
				setShouldLogout(true);
			} else if (result.timeLeft <= warningThreshold) {
				setSessionWarning({
					show: true,
					timeLeft: result.timeLeft,
				});
			}

			if (result.expired) {
				setShouldLogout(true);
			}
		}, preWarningLength);
	}, [isSessionManagementActive, timeLeft, warningThreshold, preWarningLength]);

	const extendSession = useCallback(async (): Promise<ExtendSessionResult> => {
		if (disabled) {
			return {
				success: true,
				triggerLogout: false,
				timeLeft: 2 * TIMEOUT_WARNING_LENGTH,
			};
		}

		const now = Date.now();

		if (now - lastSuccessfulExtensionRef.current < 2000) {
			return { success: true, timeLeft: 2 * TIMEOUT_WARNING_LENGTH };
		}

		try {
			const response = await fetch(`${PREFIX}/extend-session/`, {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (response.ok) {
				const data = await response.json();

				if (!data.triggerLogout && isSessionManagementActive) {
					lastSuccessfulExtensionRef.current = now;
					setSessionWarning({ show: false, timeLeft: TIMEOUT_WARNING_LENGTH });
					resetSession();
				}

				return {
					success: true,
					timeLeft: data.timeLeft,
					triggerLogout: data.triggerLogout,
				};
			} else {
				const data = await response.json().catch(() => ({
					message: `HTTP ${response.status}: ${response.statusText}`,
				}));

				if (response.status === 401 && data.triggerLogout) {
					return {
						timeLeft: 0,
						success: false,
						triggerLogout: true,
						error: data.error || 'Session expired',
					};
				} else {
					return {
						timeLeft: 0,
						success: false,
						error: data.error || `HTTP ${response.status}: ${response.statusText}`,
					};
				}
			}
		} catch (caughtError) {
			return {
				success: false,
				timeLeft: 0,
				error: caughtError instanceof Error ? caughtError.message : String(caughtError),
			};
		}
	}, [disabled, isSessionManagementActive, resetSession]);

	const performLogout = useCallback(async () => {
		await logout(eapiBaseUrl);

		setSessionWarning({ show: false, timeLeft: TIMEOUT_WARNING_LENGTH });
		setShouldLogout(false);

		if (sessionCheckIntervalRef.current) {
			clearInterval(sessionCheckIntervalRef.current);
			sessionCheckIntervalRef.current = null;
		}

		push(`/${PATIENT_SERVICES_BASE_URL}`);
	}, [push, eapiBaseUrl]);

	useEffect(() => {
		if (shouldLogout) {
			performLogout();
		}
	}, [shouldLogout, performLogout]);

	useEffect(() => {
		if (disabled) return;

		if (isSessionManagementActive) {
			startSession();
		}

		return () => {
			if (sessionCheckIntervalRef.current) {
				clearInterval(sessionCheckIntervalRef.current);
				sessionCheckIntervalRef.current = null;
			}
		};
	}, [isSessionManagementActive, startSession, disabled]);

	useEffect(() => {
		if (disabled || !isSessionManagementActive) return;

		const handleRouteChangeComplete = async () => {
			const result = await extendSession();

			if (result.triggerLogout) {
				setShouldLogout(true);
			}
		};

		handleRouteChangeComplete();
	}, [pathname, extendSession, disabled, isSessionManagementActive]);

	useEffect(() => {
		if (disabled || !isSessionManagementActive) return;

		if (sessionWarning.show) {
			const timer = setTimeout(async () => {
				const result = await timeLeft();

				if (result.triggerLogout) {
					setShouldLogout(true);
				}
			}, Math.max(0, sessionWarning.timeLeft * 1000));

			return () => clearTimeout(timer);
		}
	}, [sessionWarning.show, sessionWarning.timeLeft, disabled, isSessionManagementActive]);

	return {
		sessionWarning,
		logout: performLogout,
		isSessionManagementActive,
		extendSession: extendSession,
		triggerLogout: () => setShouldLogout(true),
		hideSessionWarning: () => setSessionWarning({ show: false, timeLeft: TIMEOUT_WARNING_LENGTH }),
	};
};
